import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { detecterLocaleNavigateur, estLocaleGeree, messagesPour, type Locale, type Messages } from '@repere/core';

const CLE = 'repere.langue';

export interface ContexteLangue {
  locale: Locale;
  messages: Messages;
  changer: (locale: Locale) => void;
}

const LangueContext = createContext<ContexteLangue | null>(null);

/** Lit la langue choisie, si `localStorage` la connaît et qu'elle est toujours gérée. */
function localeConservee(): Locale | null {
  try {
    const valeur = localStorage.getItem(CLE);
    return valeur && estLocaleGeree(valeur) ? valeur : null;
  } catch {
    return null;
  }
}

/**
 * Langue par défaut, sans rien demander : les langues déclarées par le navigateur,
 * la première qui est gérée l'emportant.
 *
 * Décision produit : pas de géolocalisation IP. Le navigateur est la seule source, et
 * `detecterLocaleNavigateur` se replie sur l'anglais — pas le français — quand aucune
 * langue déclarée n'est gérée.
 */
function localeParDefaut(): Locale {
  const declarees = typeof navigator !== 'undefined' ? (navigator.languages ?? [navigator.language]) : [];
  return detecterLocaleNavigateur(declarees);
}

/**
 * Langue de l'interface — détectée, mémorisée, et changeable à tout instant.
 *
 * **Le contexte a un repli, il ne force pas son usage.** `useTraduction()` ne lève pas
 * d'erreur hors d'un `FournisseurLangue` : il rend le français, la langue native du
 * produit. C'est un choix délibéré, différent de `useSession` qui lève — une session
 * absente cacherait un vrai bug (des données affichées sans client pour les charger),
 * alors qu'une langue absente a un repli à la fois sûr et correct : c'est déjà la langue
 * dans laquelle le produit est écrit. Cela évite surtout d'obliger chacun des composants
 * non encore traduits, et chacun de leurs tests, à se souvenir d'empiler ce fournisseur.
 */
export function FournisseurLangue({ children }: { children: ReactNode }) {
  const [locale, setLocale] = useState<Locale>(() => localeConservee() ?? localeParDefaut());

  useEffect(() => {
    // `lang` sur la racine : un lecteur d'écran choisit sa prononciation dessus, et un
    // moteur de recherche sait quelle langue il indexe.
    document.documentElement.lang = locale;
  }, [locale]);

  const changer = useCallback((choix: Locale) => {
    setLocale(choix);
    try {
      localStorage.setItem(CLE, choix);
    } catch {
      // Navigation privée, stockage refusé : le choix vaut pour la session en cours.
    }
  }, []);

  const valeur = useMemo<ContexteLangue>(
    () => ({ locale, messages: messagesPour(locale), changer }),
    [locale, changer],
  );

  return <LangueContext.Provider value={valeur}>{children}</LangueContext.Provider>;
}

const REPLI_FRANCAIS: ContexteLangue = {
  locale: 'fr',
  messages: messagesPour('fr'),
  changer: () => {
    // Hors de `FournisseurLangue`, changer de langue n'a personne à qui le dire.
  },
};

export function useTraduction(): ContexteLangue {
  return useContext(LangueContext) ?? REPLI_FRANCAIS;
}
